import React, { createContext, useContext, useState, useCallback } from 'react';
import axiosinstance from '../axios/axiosInstance';
import { LoaderContext } from './LoaderContext';

const XPContext = createContext();

export const useXP = () => {
  const context = useContext(XPContext);
  if (!context) {
    throw new Error('useXP must be used within an XPProvider');
  }
  return context;
};

export const XPProvider = ({ children }) => {
  const { showLoader, hideLoader } = useContext(LoaderContext);
  const [playerXP, setPlayerXP] = useState(null);
  const [playerLeaderboard, setPlayerLeaderboard] = useState([]);
  const [teamLeaderboard, setTeamLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get player XP
  const fetchPlayerXP = useCallback(async (userId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosinstance.get(`/xp/player/${userId}`);
      setPlayerXP(response.data);
      return response.data;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching player XP:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get player leaderboard
  const fetchPlayerLeaderboard = useCallback(async (period = 'all') => {
    try {
      setLoading(true);
      setError(null);
      showLoader();
      const response = await axiosinstance.get(`/xp/leaderboard?period=${period}`);
      setPlayerLeaderboard(response.data.leaderboard || []);
      return response.data;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching player leaderboard:', err);
      throw err;
    } finally {
      setLoading(false);
      hideLoader();
    }
  }, [showLoader, hideLoader]);

  // Get team leaderboard
  const fetchTeamLeaderboard = useCallback(async (period = 'all') => {
    try {
      setLoading(true);
      setError(null);
      showLoader();
      const response = await axiosinstance.get(`/xp/team-leaderboard?period=${period}`);
      setTeamLeaderboard(response.data.leaderboard || []);
      return response.data;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching team leaderboard:', err);
      throw err;
    } finally {
      setLoading(false);
      hideLoader();
    }
  }, [showLoader, hideLoader]);

  // Get match awards
  const fetchMatchAwards = useCallback(async (matchId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosinstance.get(`/xp/match-awards/${matchId}`);
      return response.data;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching match awards:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get player badges
  const fetchPlayerBadges = useCallback(async (userId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosinstance.get(`/xp/badges/player/${userId}`);
      return response.data;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching player badges:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get team badges
  const fetchTeamBadges = useCallback(async (teamId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosinstance.get(`/xp/badges/team/${teamId}`);
      return response.data;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching team badges:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get team performance points
  const fetchTeamPoints = useCallback(async (teamId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosinstance.get(`/xp/team/${teamId}`);
      return response.data;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching team points:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const value = {
    playerXP,
    playerLeaderboard,
    teamLeaderboard,
    loading,
    error,
    fetchPlayerXP,
    fetchPlayerLeaderboard,
    fetchTeamLeaderboard,
    fetchMatchAwards,
    fetchPlayerBadges,
    fetchTeamBadges,
    fetchTeamPoints
  };

  return <XPContext.Provider value={value}>{children}</XPContext.Provider>;
};

